import React from 'react';
import { Book } from 'lucide-react';

const MethodologyPage: React.FC = () => {
  return (
    <main className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Book className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600" />
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
          Our Halal Methodology
        </h1>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 sm:p-8">
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Introduction</h2>
          <p className="text-gray-700 leading-relaxed">
            The Halal Scanner app uses a comprehensive methodology to evaluate products based on their ingredients, additives, and certifications. Our goal is to provide Muslims with reliable information to make informed choices about the products they consume.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Halal Status Categories</h2>
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4">
              <h3 className="font-semibold text-emerald-800 mb-2">Halal</h3>
              <p className="text-gray-700">
                Products that contain only permissible ingredients and have been processed according to Islamic requirements. These products may also carry recognized halal certification.
              </p>
            </div>

            <div className="bg-red-50 border border-red-100 rounded-lg p-4">
              <h3 className="font-semibold text-red-800 mb-2">Haram</h3>
              <p className="text-gray-700">
                Products containing explicitly forbidden ingredients such as pork derivatives, alcohol, or ingredients from animals not slaughtered according to Islamic law.
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4">
              <h3 className="font-semibold text-yellow-800 mb-2">Mushbooh (Doubtful)</h3>
              <p className="text-gray-700">
                Products containing ingredients that require further verification, such as:
              </p>
              <ul className="list-disc pl-5 mt-2 text-gray-700 space-y-1">
                <li>Ingredients that could be derived from either halal or haram sources</li>
                <li>Additives with ambiguous origins</li>
                <li>Processing aids that may involve alcohol or other prohibited substances</li>
              </ul>
            </div>

            <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-2">Unknown</h3>
              <p className="text-gray-700">
                Products where insufficient information is available to determine halal status.
              </p>
            </div>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Our Evaluation Process</h2>
          
          <div className="mb-6">
            <h3 className="font-semibold text-gray-800 mb-2">Ingredient Analysis</h3>
            <p className="text-gray-700 mb-3">
              We analyze each ingredient against our database of:
            </p>
            <ul className="list-disc pl-5 text-gray-700 space-y-1">
              <li>Known haram substances (pork derivatives, alcohol, etc.)</li>
              <li>E-numbers and food additives with their respective halal status</li>
              <li>Chemical compounds commonly used in food production</li>
            </ul>
          </div>
          
          <div className="mb-6">
            <h3 className="font-semibold text-gray-800 mb-2">Certification Verification</h3>
            <p className="text-gray-700 mb-3">
              We recognize halal certifications from reputable international and local certification bodies, including:
            </p>
            <ul className="list-disc pl-5 text-gray-700 space-y-1">
              <li>IFANCA (Islamic Food and Nutrition Council of America)</li>
              <li>HMC (Halal Monitoring Committee)</li>
              <li>JAKIM (Department of Islamic Development Malaysia)</li>
              <li>And other recognized authorities</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-semibold text-gray-800 mb-2">Database Maintenance</h3>
            <p className="text-gray-700 mb-3">
              Our database of ingredients, E-numbers, and chemicals is:
            </p>
            <ul className="list-disc pl-5 text-gray-700 space-y-1">
              <li>Regularly updated based on the latest research</li>
              <li>Verified against scholarly opinions on halal compliance</li>
              <li>Cross-referenced with information from certification bodies</li>
            </ul>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Limitations</h2>
          <p className="text-gray-700 mb-3">
            Our app provides information based on available data, but we acknowledge:
          </p>
          <ul className="list-disc pl-5 text-gray-700 space-y-1">
            <li>Manufacturers may change ingredients without notice</li>
            <li>Processing methods may not be fully disclosed on labels</li>
            <li>Regional variations in products may exist under the same name</li>
          </ul>
          <p className="text-gray-700 mt-3">
            We recommend users verify critical products with manufacturers or certification bodies when needed.
          </p>
        </section>
      </div>
    </main>
  );
};

export default MethodologyPage;